import { Metadata } from 'src/types';


import { isNotEmpty, returnIsNotEmptyOrTrue } from "src/utilities/hasValue";
import { StateStore } from "./apiResouces";
import { path, filter, match, split, length, replace, forEach, pathOr, equals, join, cond, always, T } from 'ramda';
import { splitTheSavedDataPathWithDot } from "./autoAnswerConfigurator";
import { getFormatters } from "../Formatters/formatters";
import { mkUpdateApiFields } from '../Bot/botServiceUtils/preSignSaver';
import { timeStampInISOFormat } from 'src/utilities/formatDate';
import { isFunction } from 'src/utilities/isFunction';

interface ApiResourceValueConfig {
  resource: string;
  field_path: string;
  formatter?: string;
  alternate_config: {
    resource: string;
    field_path: string;
    formatter?: string;
  },
  convertor?: string;
}

/**
api_resources_config is config of each `key_field` and its config of where we can get value of that key_field
for example value of this ___borrower_name___ key_field get resource of borrower and its value from configured field_path
*/
const api_resources_config = {
  'email_in_signup': {
    'resource': 'session',
    'formatter': 'FUNCTION_CALL',
    'field_path': 'authenticatedUser.accountEmail.get'
  },
  'borrower_loan_role_id': {
    'resource': 'borrowerLoanRole',
    'field_path': 'id'
  },
  'co_borrower_loan_role_id': {
    'resource': 'coBorrowerLoanRole',
    'field_path': 'id'
  },
  'co_borrower_name': {
    'resource': 'coBorrowerName',
    'formatter': 'FULL_NAME',
    'field_path': 'profile'
  },
  'applicant_loan_role': {
    'resource': 'session',
    'formatter': 'FUNCTION_CALL',
    'field_path': 'authenticatedUser.loggedInRole.get'
  },
  'user_type': {
    'resource': 'session',
    'formatter': 'FUNCTION_CALL',
    'field_path': 'unAuthenticatedUser.userType.get'
  },
  'authorized_user_id': {
    'resource': 'session',
    'formatter': 'FUNCTION_CALL',
    'field_path': 'unAuthenticatedUser.authorizedUserID.get'
  },
  'borrower_name': {
    'resource': 'borrower',
    'formatter': 'FULL_NAME',
    'field_path': 'profile',
    'alertnate_config': {
      'resource': 'accounts',
      'formatter': 'FULL_NAME'
    }
  },
  'borrower_first_name': {
    'resource': 'borrower',
    'formatter': 'FIRST_NAME',
    'field_path': 'profile',
    'alertnate_config': {
      'resource': 'accounts',
      'formatter': 'FIRST_NAME'
    }
  },
  'borrower_address': {
    'resource': 'borrower',
    'formatter': 'ADDRESS_STRINGIFY',
    'field_path': 'addresses.present'
  },
  'business_name': {
    'resource': 'business',
    'field_path': 'business_name'
  },
  'business_owner_name': {
    'resource': 'business',
    'field_path': 'owners.0.name'
  },
  'property_address': {
    'resource': 'property',
    'formatter': 'ADDRESS_STRINGIFY',
    'field_path': 'address'
  },
  'former_address': {
    'resource': 'borrower',
    'formatter': 'ADDRESS_STRINGIFY',
    'field_path': 'addresses.former.0'
  },
  'property_loan_type': {
    'resource': 'property',
    'field_path': 'object_meta.labels.property_loan_type'
  },
  'employer_name': {
    'resource': 'employment',
    'field_path': 'history.name'
  },
  'employer_address': {
    'resource': 'employment',
    'formatter': 'ADDRESS_STRINGIFY',
    'field_path': 'history.address'
  },
  'self_employment_business_name': {
    'resource': 'employment',
    'field_path': 'history.name'
  },
  'cashout_purpose': {
    'resource': 'property',
    'field_path': 'object_meta.labels.cashout_purpose',
  },
  'collateral_loan': {
    'resource': 'property',
    'field_path': 'object_meta.labels.collateral_loan'
  },
  'lender_name': {
    'resource': 'borrower',
    'field_path': 'object_meta.labels.lender_name'
  },
  'other_lender_name': {
    'resource': 'borrower',
    'field_path': 'object_meta.labels.other_lender_name'
  },
  'type_of_id': {
    'resource': 'business',
    'field_path': 'metadata.type_of_id'
  },
  'id_issuing_authority': {
    'resource': 'business',
    'field_path': 'metadata.id_issuing_authority'
  },
  'applicant_name': {
    'resource': 'borrower',
    'field_path': 'profile.name',
    "no_auth": 'true'
  },
  'applicant_phone': {
    'resource': 'accounts',
    'field_path': 'cellmobile_phone',
    "no_auth": 'true'
  },
  'applicant_email': {
    'resource': 'accounts',
    'field_path': 'email',
    "no_auth": 'true'
  },
  'card_type': {
    'resource': 'session',
    'formatter': 'FUNCTION_CALL',
    'field_path': 'unAuthenticatedUser.productType.get'
  },
  'vin': {
    'resource': 'primary_collateral',
    'field_path': 'asset_details.0.vehicle_identification_number',
    'convertor': 'array'
  },
  'advisor_first_name': {
    'resource': 'advisorResource',
    'field_path': 'first_name',
  },
  'advisor_last_name': {
    'resource': 'advisorResource',
    'field_path': 'last_name',
  },
  'advisor_email': {
    'resource': 'advisorResource',
    'field_path': 'email',
  },
  'this': {
    "field_path":"this"
  },
}

const general_custom_functions = {
  'now': () => timeStampInISOFormat()
}

const TRIPLE_UNDERSCORE = '___';


/**
this class is to identify the configured get the api resource value of key_field from store
*/

class APIResourceValueEvaluator {
  enclosure: string = TRIPLE_UNDERSCORE;
  store: StateStore;
  dynamicFields: Metadata;
  constructor(store: StateStore, dynamicFields: Metadata = {}, enclosure?: string) {
    this.store = store
    this.enclosure = returnIsNotEmptyOrTrue(enclosure, enclosure, this.enclosure)
    this.dynamicFields = dynamicFields
  }

  replace(replaceableString: string,displayerValue?:string):string {
    const replacer = new FieldReplacer(
      this.store,
      this.enclosure,
      this.dynamicFields,
      replaceableString,
    )
    return replacer.replace(displayerValue)
  }
}

class FieldReplacer {
  enclosure: string;
  store: StateStore;
  value: string;
  fields: string[];
  dynamicFields: Metadata;
  regexp: string;
  displayerValue: any;

  constructor(
    store: StateStore,
    enclosure: string,
    dynamicFields: any,
    replaceableString: string,
  ) {
    this.store = store
    this.enclosure = enclosure
    this.dynamicFields = dynamicFields
    this.regexp = `${this.enclosure}(.*)${this.enclosure}`
    this.value = replaceableString
    this.fields = this.getFields(replaceableString)
  }

  getFields(value: string) {
    return filter(
      (s: string) => length(match(new RegExp(this.regexp), s)) > 0,
      split(' ', value)
    )
  }

  canReplace() {
    return isNotEmpty(this.fields)
  }

  trim(field: string) {
    return {
      keyField: pathOr('', [1], split(this.enclosure, field)),
      trimmedField: join("", split(this.enclosure, field))
    }
  }

  apiFieldConfig(field: string) {
    return path([field], api_resources_config)
  }

  generalFieldConfig(field: string) {
    return path([field], general_custom_functions)
  }

  getDynamicFields(field: string) {
    return path([field], this.dynamicFields)
  }

  replace(displayerValue?:any) {
    let value = this.value

    this.canReplace() && forEach((field: string) => {
      const { trimmedField, keyField } = this.trim(field)
      const apiConfig: any = this.apiFieldConfig(keyField)
      const dynamicValue: any = this.getDynamicFields(field)
      const finder = new ApiResourceValueFinder(this.store, apiConfig);
      const fn: any = this.generalFieldConfig(keyField)
      const updatedValue = cond([
        [() => isNotEmpty(dynamicValue), always(replace(field, dynamicValue, value))],
        [() => isNotEmpty(apiConfig), () => {
          const fieldValue = replace(keyField, displayerValue || finder.value(), trimmedField)
          if (isNotEmpty(fieldValue)) {
            return this.dataConvertor(replace(field, fieldValue, value), pathOr('', ['convertor'], apiConfig))
          }
          return value
        }],
        [() => isFunction(fn), () => replace(field, fn(), value)],
        [T, always(value)]
      ])(keyField)
      value = updatedValue;

    }, this.fields)

    return value
  }

  private dataConvertor(value: any, convertType: any) {
    return cond([
      [equals('array'), always([value])],
      [T, always(value)],
    ])(convertType);
  }
}


class ApiResourceValueFinder {
  config?: ApiResourceValueConfig
  store: StateStore;

  constructor(store: StateStore, config?: ApiResourceValueConfig) {
    this.store = store;
    this.config = config;
  }

  resource(config: any, resourceFinder: any = {}) {
    return this.store.findResource(resourceFinder, pathOr('', ['resource'], config))
  }

  alternateResourceValue() {
    const config = pathOr({}, ['alertnate_config'], this.config)
    return isNotEmpty(config) ? this.__resourceValue(config) : ''
  }

  __resourceValue(config: any, resourceFinder?: any) {
    const resource = equals(pathOr('', ['no_auth'], config), 'true') ?
      pathOr('', [pathOr('', ['resource'], config)], mkUpdateApiFields()) :
      this.resource(config)
    const fieldPath = pathOr('', ['field_path'], config);
    const value = fieldPath ? pathOr('', splitTheSavedDataPathWithDot(fieldPath), resource) : resource;
    return isNotEmpty(value) ? getFormatters(pathOr('', ['formatter'], config))(value) : '';
  }

  resourceValue(resourceFinder?: any) {
    const value = this.__resourceValue(this.config, resourceFinder)
    const resourceValue = returnIsNotEmptyOrTrue(
      value,
      value,
      this.alternateResourceValue()
    )
    return resourceValue
  }

  value(resourceFinder?: any) {
    return returnIsNotEmptyOrTrue(
      this.config,
      this.resourceValue(resourceFinder),
      ''
    )
  }

}

export {
  APIResourceValueEvaluator
}
